// To Do as the worklist (docs/spec-todo-worklists.md, sql/todo-worklists.sql): type, department, component, location,
// responsible, closed date. One set of rules for the To Do page, the AI (api/_agent.js), the daily e-mail (api/_mail.js)
// and the Dashboard. Plain JS so tests/todo-worklists.test.mjs can import it.
export const TODO_TYPES = ['Defect', 'Maintenance', 'Admin', 'General']
export const TYPE_TABS = [['', 'All'], ['Defect', 'Defects'], ['Maintenance', 'Maintenance'], ['Admin', 'Admin'], ['General', 'General']]
export const DEPARTMENTS = ['Deck', 'Engineering', 'Interior', 'Bridge', 'Galley', 'Management']
// suggestions for "Person responsible" when nobody in the crew list fits (free text, anything else is fine too)
export const RESPONSIBLE_ROLES = ['Shipyard', 'Contractor', 'Captain/Engineer', 'Management company']
export const NEW_FIELDS = ['type', 'department', 'equipment_id', 'location_text', 'responsible_text', 'closed_at', 'closed_by']

const low = s => String(s == null ? '' : s).trim().toLowerCase()
// "defect" / "Defects" / "maintenance" -> the stored value; anything else -> null
export function normType(t) { const k = low(t).replace(/s$/, ''); return TODO_TYPES.find(x => x.toLowerCase() === k) || null }
// "engine" / "ER" is not a department; "engineering" / "Deck" / "exterior" are (crew list uses Exterior for deck crew)
export function normDept(d) {
  const k = low(d)
  if (!k) return null
  if (k === 'exterior') return 'Deck'
  if (k === 'engine' || k === 'engine room') return 'Engineering'
  if (k === 'navigation') return 'Bridge'
  return DEPARTMENTS.find(x => x.toLowerCase() === k) || null
}
// the database error before sql/todo-worklists.sql has run (a write of one of the new columns)
export const missingWorklist = text => NEW_FIELDS.some(f => String(text || '').includes(f)) && /42703|does not exist|PGRST204|schema cache|column/i.test(String(text || ''))
export const MISSING_HINT = 'The worklist fields need sql/todo-worklists.sql - run it in Supabase first.'

export const isClosed = i => !!(i && i.done)
export const statusOf = i => (isClosed(i) ? 'Closed' : 'Open')
// GA pin with a zone -> the zone name; else the free text
export function locationOf(i, zoneName) { const z = i && i.zone_id && zoneName ? zoneName(i.zone_id) : ''; return z || (i && i.location_text) || '' }
export function responsibleOf(i) { return [i && i.assignee, i && i.responsible_text].filter(Boolean).join(' · ') }
export const dayOf = ts => (ts ? String(ts).slice(0, 10) : '')
export const typeOf = i => (i && i.type) || 'General'

// filters: { q, department, equipment, list, location, status: 'open' (default) | 'closed' | 'all', type }
export const EMPTY_FILTERS = { q: '', department: '', equipment: '', list: '', location: '', status: 'open', type: '' }
// names: { list(id), equipment(id), zone(id) } -> text, for search and location
export function applyFilters(items, f, names = {}) {
  const F = { ...EMPTY_FILTERS, ...(f || {}) }
  const q = low(F.q), loc = low(F.location)
  return (items || []).filter(i => {
    if (F.status === 'open' && isClosed(i)) return false
    if (F.status === 'closed' && !isClosed(i)) return false
    if (F.type && typeOf(i) !== F.type) return false
    if (F.department && (i.department || '') !== F.department) return false
    if (F.equipment && String(i.equipment_id || '') !== String(F.equipment)) return false
    if (F.list && String(i.list_id || '') !== String(F.list)) return false
    const where = locationOf(i, names.zone)
    if (loc && !low(where).includes(loc)) return false
    if (q) {
      const hay = [i.text, i.notes, where, responsibleOf(i), i.department, typeOf(i), names.equipment ? names.equipment(i.equipment_id) : '', names.list ? names.list(i.list_id) : ''].map(low).join(' \u0001 ')
      if (!hay.includes(q)) return false
    }
    return true
  })
}
export const filtersActive = f => Object.entries({ ...EMPTY_FILTERS, ...(f || {}) }).some(([k, v]) => v !== EMPTY_FILTERS[k])

// the task list, grouped by date: Overdue, Today, This week (to Sunday), Later, No date; closed tasks last. A closed task is never
// "overdue". Keeps the order it is given (the chosen sort). today = 'YYYY-MM-DD' (ship's date).
export const DATE_GROUPS = [['overdue', 'Overdue'], ['today', 'Today'], ['week', 'This week'], ['later', 'Later'], ['none', 'No date'], ['closed', 'Closed']]
export function endOfWeek(today) {
  const d = new Date(today + 'T12:00:00')
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7))   // getDay: Sunday 0 (a Sunday's week ends that day)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}
export function dateGroupOf(item, today) {
  if (isClosed(item)) return 'closed'
  const due = item && item.due_date
  if (!due) return 'none'
  if (due < today) return 'overdue'
  if (due === today) return 'today'
  return due <= endOfWeek(today) ? 'week' : 'later'
}
export function groupByDate(items, today) {
  const by = {}
  for (const i of items || []) (by[dateGroupOf(i, today)] = by[dateGroupOf(i, today)] || []).push(i)
  return DATE_GROUPS.filter(([k]) => by[k]).map(([key, label]) => ({ key, label, items: by[key] }))
}
// the summary cards count every open task of the vessel, whatever view is open
export function summaryCounts(items, today) {
  const open = (items || []).filter(i => !isClosed(i))
  return {
    overdue: open.filter(i => dateGroupOf(i, today) === 'overdue').length,
    today: open.filter(i => dateGroupOf(i, today) === 'today').length,
    defects: open.filter(i => typeOf(i) === 'Defect').length,
    high: open.filter(i => i.priority === 3).length,
  }
}
export const initialsOf = name => String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('')

// remembered per login (localStorage; any failure = nothing remembered, never a throw)
export const filterKey = who => 'hc_todo_filters:' + (who || 'anon')
export function loadFilters(who, storage) {
  try {
    const s = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    const v = s && JSON.parse(s.getItem(filterKey(who)) || 'null')
    if (!v || typeof v !== 'object' || Array.isArray(v)) return { ...EMPTY_FILTERS }
    const out = { ...EMPTY_FILTERS }
    for (const k of Object.keys(EMPTY_FILTERS)) if (typeof v[k] === 'string') out[k] = v[k]
    if (!['open', 'closed', 'all'].includes(out.status)) out.status = 'open'
    if (out.type && !TODO_TYPES.includes(out.type)) out.type = ''
    return out
  } catch (e) { return { ...EMPTY_FILTERS } }
}
export function saveFilters(who, f, storage) {
  try {
    const s = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    if (s) s.setItem(filterKey(who), JSON.stringify({ ...EMPTY_FILTERS, ...(f || {}) }))
  } catch (e) { /* private window / blocked storage: not remembered */ }
}

// Bulk "Update tasks": the chosen fields -> one patch for every selected item. Empty = leave as it is; '-' = clear.
// fields: { status: 'open' | 'closed', assignee, list_id, due_date, type }
export function bulkPatch(fields) {
  const p = {}, x = fields || {}
  if (x.status === 'closed') p.done = true
  else if (x.status === 'open') p.done = false
  if (x.assignee === '-') p.assignee = null; else if (x.assignee) p.assignee = x.assignee
  if (x.list_id) p.list_id = x.list_id
  if (x.due_date === '-') p.due_date = null; else if (x.due_date && /^\d{4}-\d{2}-\d{2}$/.test(x.due_date)) p.due_date = x.due_date
  if (x.type && TODO_TYPES.includes(x.type)) p.type = x.type
  return p
}
// what the page shows at once (the database stamps closed_at itself; this keeps the row right until the next load)
export function closedPatch(item, done, now = new Date().toISOString()) { return done ? { done: true, closed_at: (item && item.closed_at) || now } : { done: false, closed_at: null, closed_by: null } }

// Export (Excel / CSV): every column, one row per item
export const EXPORT_COLUMNS = ['Title', 'Type', 'Component', 'Department', 'Location', 'Responsible', 'Created', 'Due', 'Closed', 'Status', 'Priority', 'List']
const PRIORITY_LABEL = { 3: 'High', 2: 'Medium', 1: 'Low', 0: 'None' }
export function exportRows(items, names = {}) {
  return [EXPORT_COLUMNS, ...(items || []).map(i => [
    i.text || '', typeOf(i), names.equipment ? names.equipment(i.equipment_id) || '' : '', i.department || '', locationOf(i, names.zone),
    responsibleOf(i), dayOf(i.created_at), i.due_date || '', dayOf(i.closed_at), statusOf(i), PRIORITY_LABEL[i.priority || 0] || 'None',
    names.list ? names.list(i.list_id) || '' : '',
  ])]
}
// CSV that Excel opens right: UTF-8 BOM, CRLF, every cell quoted when needed, formula-looking text defused
export function toCsv(rows) {
  const cell = v => {
    let s = String(v == null ? '' : v)
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s
    return /[",\r\n;]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }
  return '﻿' + (rows || []).map(r => r.map(cell).join(',')).join('\r\n') + '\r\n'
}
export const exportName = (vessel, title, ext) => (String(vessel || 'Vessel') + '_' + String(title || 'Worklist') + '_' + new Date().toISOString().slice(0, 10)).replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '') + '.' + ext

// open Defects per department (Dashboard card, daily e-mail): [[department, n]] most first; no department = "Unassigned"
export function openDefectsByDept(items) {
  const m = new Map()
  for (const i of items || []) {
    if (isClosed(i) || typeOf(i) !== 'Defect' || i.parent_id) continue
    const d = i.department || 'Unassigned'
    m.set(d, (m.get(d) || 0) + 1)
  }
  const order = d => { const k = DEPARTMENTS.indexOf(d); return k < 0 ? 99 : k }
  return [...m.entries()].sort((a, b) => (b[1] - a[1]) || (order(a[0]) - order(b[0])))
}

// ---- History (all closed items): closed date range, closed by, time open (sql/todo-worklists.sql closed_at / closed_by) ----
export const HISTORY_RANGES = [['week', 'This week'], ['month', 'This month'], ['3months', 'Last 3 months'], ['custom', 'Custom dates']]
const iso = d => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
// key + today (YYYY-MM-DD) -> { from, to } inclusive days; custom: the given from / to; '' = any time
export function closedRange(key, today, custom = {}) {
  const t = new Date(today + 'T12:00:00')
  if (key === 'week') { const d = new Date(t); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return { from: iso(d), to: today } }   // from Monday
  if (key === 'month') return { from: today.slice(0, 8) + '01', to: today }
  if (key === '3months') { const d = new Date(t); d.setMonth(d.getMonth() - 3); return { from: iso(d), to: today } }
  if (key === 'custom') return { from: custom.from || '', to: custom.to || '' }
  return { from: '', to: '' }
}
// closed items, newest closed first, in the range and by that person
export function historyItems(items, { from = '', to = '', closedBy = '' } = {}) {
  return (items || []).filter(i => isClosed(i) && !i.parent_id)
    .filter(i => { const d = dayOf(i.closed_at); return (!from || (d && d >= from)) && (!to || (d && d <= to)) })
    .filter(i => !closedBy || (i.closed_by || '') === closedBy)
    .sort((a, b) => String(b.closed_at || '').localeCompare(String(a.closed_at || '')))
}
// created -> closed, whole days (null when either date is missing)
export function daysOpen(i) {
  if (!i || !i.created_at || !i.closed_at) return null
  const ms = Date.parse(i.closed_at) - Date.parse(i.created_at)
  return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 86400000)) : null
}
export const HISTORY_COLUMNS = ['Closed', 'Title', 'Type', 'Department', 'Component', 'Location', 'Responsible', 'Closed by', 'Days open']
export function historyRows(items, names = {}) {
  return [HISTORY_COLUMNS, ...(items || []).map(i => [dayOf(i.closed_at), i.text || '', typeOf(i), i.department || '', names.equipment ? names.equipment(i.equipment_id) || '' : '',
    locationOf(i, names.zone), responsibleOf(i), i.closed_by || '', daysOpen(i) == null ? '' : daysOpen(i)])]
}
// "Engineering defects closed in October 2026" - the report title from the filters
export function historyTitle(f = {}, range = {}) {
  const NOUN = { Defect: 'defects', Maintenance: 'maintenance jobs', Admin: 'admin tasks', General: 'general tasks' }
  const what = [f.department, NOUN[f.type] || 'tasks'].filter(Boolean).join(' ')
  const m = s => new Date(s + 'T12:00:00').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
  const d = s => new Date(s + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  let when = ''
  if (range.from && range.to && range.from.slice(0, 7) === range.to.slice(0, 7) && range.from.endsWith('-01')) when = ' in ' + m(range.from)
  else if (range.from && range.to) when = ' ' + d(range.from) + ' to ' + d(range.to)
  else if (range.from) when = ' since ' + d(range.from)
  else if (range.to) when = ' until ' + d(range.to)
  const t = what + ' closed' + when
  return t.charAt(0).toUpperCase() + t.slice(1)
}
